// useSidebarBucketsData.js

import { useEffect, useState } from 'react';

// Services
import { subscribeToWorkspaceChannels } from 'channels/services/ChannelReadService';
import { canUserSeeChannel } from 'channels/utils/channelVisibility';
import { subscribeToDatabasesShared } from 'services/databases/DatabaseReadService';
import { subscribeToDashboards } from 'services/dashboards/DashboardReadService';
import { subscribeToInstalledModules } from 'services/templates/TemplateRuntimeService';

/**
 * Subscribes sidebar bucket source rows for the active workspace.
 *
 * @param {string} workspaceKey - Active workspace key.
 * @param {string} viewerUserKey - Active user key for visibility filtering.
 * @returns {object} Modules, databases, dashboards, and module hydration state.
 */
const useSidebarBucketsData = ({ workspaceKey, viewerUserKey }) => {
    // State
    const [modules, setModules] = useState([]);
    const [modulesHydrated, setModulesHydrated] = useState(false);
    const [databases, setDatabases] = useState([]);
    const [dashboards, setDashboards] = useState([]);
    const [channels, setChannels] = useState([]);

    // Effects

    // Subscribe installed modules.
    useEffect(() => {
        if (!workspaceKey) {
            setModules([]);
            setModulesHydrated(false);
            return undefined;
        }

        setModulesHydrated(false);

        const unsubscribe = subscribeToInstalledModules(workspaceKey, (rows) => {
            setModules(Array.isArray(rows) ? rows : []);
            setModulesHydrated(true);
        });

        return () => unsubscribe?.();
    }, [workspaceKey]);

    // Subscribe workspace databases.
    useEffect(() => {
        if (!workspaceKey) {
            setDatabases([]);
            return undefined;
        }

        const unsubscribe = subscribeToDatabasesShared(workspaceKey, (rows) => {
            setDatabases(Array.isArray(rows) ? rows : []);
        });

        return () => unsubscribe?.();
    }, [workspaceKey]);

    // Subscribe workspace dashboards.
    useEffect(() => {
        if (!workspaceKey) {
            setDashboards([]);
            return undefined;
        }

        const unsubscribe = subscribeToDashboards(workspaceKey, (rows) => {
            setDashboards(Array.isArray(rows) ? rows : []);
        }, { scope: 'GLOBAL' });

        return () => unsubscribe?.();
    }, [workspaceKey]);

    // Subscribe workspace channels.
    useEffect(() => {
        if (!workspaceKey || !viewerUserKey) {
            setChannels([]);
            return undefined;
        }

        const unsubscribe = subscribeToWorkspaceChannels(
            workspaceKey,
            (rows) => {
                const cleanRows = Array.isArray(rows) ? rows : [];
                setChannels(cleanRows.filter((row) => canUserSeeChannel(row, viewerUserKey)));
            },
            undefined,
            {
                viewerUserKey,
            }
        );

        return () => unsubscribe?.();
    }, [viewerUserKey, workspaceKey]);

    return {
        modules,
        modulesHydrated,
        databases,
        dashboards,
        channels,
    };
};

export default useSidebarBucketsData;